import { cn } from '@my/ui';
import type { ReactNode } from 'react';

/** Dark section band with an amber uppercase label — mirrors the cockpit mock
 *  while staying on the slate/amber TMS palette. */
export function SectionBand({
  label,
  right,
  className,
  tabs,
  children,
}: {
  label: string;
  right?: ReactNode;
  /** Layout class for the section in its flex parent, e.g. "shrink-0" or "flex-1 min-h-0". */
  className?: string;
  /** Optional tab strip rendered in place of the label (see the resource strip). */
  tabs?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-md border border-slate-200 bg-white',
        className,
      )}
    >
      {/* The same dark bar as the Orders grid's header: 11px semibold, py-2,
          and the grid's line height (its table is text-xs, so 11px text sits
          on 1.333 rather than the page's 1.5). */}
      <div className="flex shrink-0 items-center justify-between gap-2 bg-slate-700 px-3 py-2 leading-[calc(1/0.75)]">
        {tabs ?? (
          <span className="text-[11px] font-semibold uppercase tracking-wide text-white">
            {label}
          </span>
        )}
        {right && <div className="flex items-center gap-2 text-[11px] text-slate-300">{right}</div>}
      </div>
      {children}
    </div>
  );
}

const CAP_ACCENT: Record<string, string> = {
  ADR: 'bg-amber-100 text-amber-700',
  HAZ: 'bg-red-100 text-red-700',
  TEMP: 'bg-sky-100 text-sky-700',
  GDP: 'bg-emerald-100 text-emerald-700',
  FOOD: 'bg-lime-100 text-lime-700',
  'Full Load': 'bg-blue-100 text-blue-700',
  'GDP Refrig': 'bg-emerald-100 text-emerald-700',
};

/**
 * Capability chip. With no `variant` it colours by label (cargo tags in the
 * Job Queue). Pass `variant` to force the palette used on the Resources page:
 * blue for certifications/compliance, amber for service tags.
 */
export function CapBadge({ label, variant }: { label: string; variant?: 'cert' | 'tag' }) {
  const accent =
    variant === 'cert'
      ? 'bg-blue-100 text-blue-700'
      : variant === 'tag'
        ? 'bg-amber-100 text-amber-700'
        : (CAP_ACCENT[label] ?? 'bg-slate-100 text-slate-600');
  return (
    <span className={cn('inline-block rounded px-1.5 py-0.5 text-[10px] font-medium', accent)}>
      {label}
    </span>
  );
}
